import * as React from "react";
import "../../.././../assets/scss/components/toc/orderPlacement/generic/opTotalPeriod.scss";
import DeviceContext from "../../../service/indentifyDevice";
import indexer from "./../../../types/genericTypes";
import { required, isValidRegex, grtrThanEqTo, lessThanEqTo, multipleValidation } from "./../../../utils/validation";
import { integerExp } from "./../../../utils/regex";
import { EArrowKeys } from "../opConstants";

export interface IOpTotalPeriodProps {
	defaultValue?: number;
	label: string;
	id: string;
	helpTextGrey?: string;
	helpTextBlue?: string;
	errorText?: string | null; // error text
	required?: boolean; // true if is required
	integer?: boolean; // true if only integer can be entered
	float?: RegExp; // number of digits after decimal
	min?: number; // minimum number, if '-' is to be disabled on less than minimum count
	max?: number; // maximum number, if '+' is to be disabled on more than maximum count
	multiple?: number; // multiple value
	onChange: (value: number) => void; // function to be called on change
	onBlur: (error: indexer) => void; // function to be called on blur, all errors will be available in error object
}

const OpTotalPeriod: React.FC<IOpTotalPeriodProps> = props => {
	// sets the default value of input if present
	// value: value of the input (type = string; because input box returns string)
	// setValue: function to set value
	const [value, setValue] = React.useState(props.defaultValue?.toString() || "");

	// when focussed, this helps to colour the border of input_field_cont
	const [focusClass, setFocusClass] = React.useState("");

	const isMobile = DeviceContext().isMobile;

	// if multiple is present, then take it otherwise keep it 1
	const multiple = props.multiple || 1;

	/**
	 * @param value The string that needs to be checked for integer
	 * @returns If integer need not be checked, then return true.
	 * - If integer needs to be checked, then
	 * - If value is valid integer, then return true; otherwise, return false
	 */
	function isValidInteger(value: string): boolean {
		return !props.integer || (props.integer && !isValidRegex(value, integerExp));
	}

	/**
	 * @param value The string that needs to be checked for float
	 * @returns If float need not be checked, then return true.
	 * - If float needs to be checked, then
	 * - if value is valid float, then return true; otherwise, return false
	 */
	function isValidFloat(value: string): boolean {
		return !props.float || (props.float && !isValidRegex(value, props.float));
	}

	/**
	 * @param value The value that is required
	 * @returns If required need not be checked, then return true.
	 * - If required needs to be checked, return true if value is not present; false if value is present
	 */
	function isRequired(value: any): boolean {
		return !props.required || (props.required && !!required(value));
	}

	/**
	 * @param value The value that is to be validated
	 * @returns If min need not be checked, then return true.
	 * - If min needs to be checked, return true if value < min; false if value >= min
	 */
	function isLessThanMin(value: string): boolean {
		return props.min === undefined || !!grtrThanEqTo(+value, props.min);
	}

	/**
	 * @param value The value that is to be validated
	 * @returns If max need not be checked, then return true.
	 * - If max needs to be checked,
	 * - throw error if value is string
	 * - return true if value > max; false if value <= min
	 */
	function isGrtrThanMax(value: string): boolean {
		return props.max === undefined || !!lessThanEqTo(+value, props.max);
	}

	/**
	 * @param value The value that is to be validated
	 * @returns true if value is multiple of `multiple`; false otherwise
	 */
	function isMultiple(value: string): boolean {
		return !!multipleValidation(+value, multiple);
	}

	/**
	 * This function runs on blur.
	 * This removes the focus class on input_field_cont and removes commas.
	 * This function calls the onBlur callback function with all the errors.
	 */
	function handleBlur() {
		setFocusClass("");
		value && setValue((+value).toLocaleString("en-IN")); // add commas

		let errorObj: indexer = {};

		if (props.required) {
			errorObj = { ...errorObj, required: isRequired(value) }; // set isRequired error
		}
		if (props.min) {
			errorObj = { ...errorObj, min: isLessThanMin(value) }; // set error for min
		}
		if (props.max) {
			errorObj = { ...errorObj, max: isGrtrThanMax(value) }; // set error for max
		}
		if (props.multiple) {
			errorObj = { ...errorObj, multiple: isMultiple(value) }; // set error for multiple
		}
		props.onBlur(errorObj);
	}

	/**
	 * Checks for all the validations and then changes value accordingly
	 * @param e The input event
	 */
	function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
		let resolvedValue: string;
		if (
			(+e.target.value || e.target.value === "" || e.target.value === "0") &&
			isValidInteger(e.target.value) &&
			isValidFloat(e.target.value)
		) {
			// if value needs to be changed then process the new value
			resolvedValue = e.target.value;
		} else {
			// otherwise process the previous state
			resolvedValue = value;
		}

		setValue(resolvedValue || ""); // set the new value
		props.onChange(+resolvedValue); // call onchange callback
	}

	/**
	 * If `value` is not a multiple of `roundTo`, then return the largest possible value that is multiple of `roundTo`
	 * @param value value to be rounded
	 * @param roundTo round to this number
	 * @returns rounded number
	 */
	function customFloor(value: number, roundTo: number) {
		return Math.floor(value / roundTo) * roundTo;
	}

	/**
	 * On up and down key, change the value by multiple
	 * @param e keypress event
	 */
	function onArrowKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
		// find the new value
		let resolvedValue: number = +value;
		if (e.keyCode === EArrowKeys.UpArrow) {
			resolvedValue = customFloor(+value, multiple) + multiple;
		} else if (e.keyCode === EArrowKeys.DownArrow) {
			resolvedValue = customFloor(+value, multiple) - multiple;
		}

		// check for min/max validations
		if (
			(props.min !== undefined && isLessThanMin(resolvedValue.toString())) ||
			(props.max !== undefined && isGrtrThanMax(resolvedValue.toString()))
		) {
			props.onChange(+value);
			return;
		}

		// if valid, then set the new value
		setValue(resolvedValue.toString() || ""); // set the new value
		props.onChange(resolvedValue); // call onchange callback
	}

	function increaseCount() {
		const newValue = customFloor(+value, multiple) + multiple;

		setValue(newValue.toString());
		props.onChange(newValue);
	}

	function decreaseCount() {
		const newValue = customFloor(+value, multiple) - multiple;

		setValue(newValue.toString());
		props.onChange(newValue);
	}

	return (
		<div className="op_total_period">
			<label className="label ib grey" htmlFor={props.id}>
				{props.label} {isMobile && "(" + props.helpTextGrey + ")"}
			</label>
			<div className="input_field_wrapper ib">
				<div className={"input_field_cont" + (props.errorText ? " error" : focusClass)}>
					<div className="input_prefix">
						<button
							className={
								"icon edel-icon-minus" + ((props.min !== undefined && props.min >= +value) || props.errorText ? " disabled" : focusClass)
							}
							onClick={decreaseCount}
						></button>
					</div>
					<input
						id={props.id}
						value={value}
						onChange={handleChange}
						placeholder="0"
						className="input_field"
						onFocus={() => setFocusClass(" focus")}
						onBlur={handleBlur}
						onKeyDown={onArrowKeyPress}
					/>
					<div className="input_suffix">
						<button
							className={
								"icon edel-icon-plus" + ((props.max !== undefined && props.max <= +value) || props.errorText ? " disabled" : focusClass)
							}
							onClick={increaseCount}
						></button>
					</div>
				</div>
			</div>
			{!isMobile && <p className="help_text grey ib help_text_grey">{props.helpTextGrey}</p>}
			{props.errorText ? (
				<p className="help_text red error_text">{props.errorText}</p>
			) : (
				<p className="help_text blue ib">{props.helpTextBlue}</p>
			)}
		</div>
	);
};

export default OpTotalPeriod;
